import {
  initialize,
  Event as SDKEvent,
  Target,
  Result,
  VariationValue,
  Evaluation
} from '@harnessio/ff-javascript-client-sdk';

export interface FeatureFlag<T = boolean> {
  value: T;
  changeHandler?: (_value: T) => void | undefined;
}

// Type mapping utility to convert a type map to feature flag types
export type FeatureFlagMapping<T extends Record<string, VariationValue>> = {
  [K in keyof T]: FeatureFlag<T[K]>;
};

export type FeatureFlags<T extends Record<string, VariationValue> = Record<string, boolean>> = {
  [K in keyof T]: FeatureFlag<T[K]>;
};

let client: Result | undefined;
let detachListeners: (() => void) | undefined;

function sanitizeNameString(inputString: string): string {
  const pattern: RegExp = /^[A-Za-z0-9.@_-]*$/;
  if (pattern.test(inputString)) {
    return inputString;
  }
  const cleanedString: string = inputString.replace(/[^A-Za-z0-9.@_-]/g, '');
  return cleanedString;
}

function sanitizeIdentifierString(inputString: string): string {
  const pattern: RegExp = /^[\\p{L}\\d .@_-]*$/;
  if (pattern.test(inputString)) {
    return inputString;
  }
  const cleanedString: string = inputString.replace(/^[\\p{L}\\d .@_-]*$/g, '');
  return cleanedString;
}

export const init = <T extends Record<string, VariationValue> = Record<string, boolean>>(
  apiKey: string,
  target: Target,
  featureFlags: FeatureFlags<T>,
  {
    ready = (_flags: T) => {},
    change = (_changeInfo: Evaluation) => {},
    errored = (_error: unknown) => {},
    errorAuth = (_error: unknown) => {}
  },
  options: {
    enableStreaming?: boolean;
  } = {
    enableStreaming: true
  }
) => {
  if (client) {
    console.log('Feature flags already initialized');
    detachListeners?.();
    client.close();
  }

  // Sanitize target
  target.identifier = sanitizeIdentifierString(target.identifier);
  if (target?.name) {
    target.name = sanitizeNameString(target?.name);
  }

  // Held locally as well as on the module singleton so this init's callbacks keep
  // evaluating against their own client after a later init replaces the singleton.
  const ffClient = initialize(apiKey, target, {
    cache: true,
    debug: true,
    streamEnabled: options?.enableStreaming
  });
  client = ffClient;

  const dispatched = new Set<string>();

  const updateFlag = (flag: string) => {
    if (!Object.prototype.hasOwnProperty.call(featureFlags, flag)) {
      return;
    }
    // ! Will be very complex to define type for this
    const value = ffClient.variation(flag, featureFlags[flag].value) as any;
    // Dispatch on the first evaluation even when the value equals the declared
    // default, otherwise a flag defaulting to `true` never reaches consumer state
    // and reads back as `false`. Later evaluations only dispatch on real changes.
    if (dispatched.has(flag) && featureFlags[flag].value === value) {
      return;
    }
    dispatched.add(flag);
    featureFlags[flag].value = value;
    featureFlags[flag].changeHandler?.(value);
  };

  // Evaluate every declared flag rather than only the ones Harness returned, so a
  // flag missing from the evaluation payload still resolves to its declared default.
  const setFlags = () => {
    Object.keys(featureFlags).forEach(updateFlag);
  };

  function onReady(flags: Record<string, VariationValue>) {
    console.log('READY', flags);
    ready(flags as T);
    setFlags();
  }

  function onChange(changeInfo: Evaluation) {
    console.log('CHANGED', changeInfo.flag, changeInfo.value);
    change(changeInfo);
    updateFlag(changeInfo.flag);
  }

  function onError(error: unknown) {
    console.log('ERROR', error);
    errored(error);
  }

  function onErrorAuth(error: unknown) {
    console.log('ERROR AUTH', error);
    errorAuth(error);
    //now call all features flag handle change with default value
    Object.keys(featureFlags).forEach(flag => {
      dispatched.add(flag);
      featureFlags[flag].changeHandler?.(featureFlags[flag].value);
    });
  }

  // Listen for events
  // Event happens when connection to server is established
  ffClient.on(SDKEvent.READY, onReady);

  // Event happens when a changed event is pushed
  ffClient.on(SDKEvent.CHANGED, onChange);

  ffClient.on(SDKEvent.ERROR_AUTH, onErrorAuth);

  ffClient.on(SDKEvent.ERROR, onError);

  detachListeners = () => {
    ffClient.off(SDKEvent.READY, onReady);
    ffClient.off(SDKEvent.CHANGED, onChange);
    ffClient.off(SDKEvent.ERROR_AUTH, onErrorAuth);
    ffClient.off(SDKEvent.ERROR, onError);
  };
};
